import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { useSessionStore } from '../../../shell/session/store/useSessionStore';
import { useAuthStore } from '../../../auth/store/useAuthStore';
import { useBranchPermission } from '../../../shell/session/hooks/useBranchPermission';
import { useModulePermission } from '../../../shell/session/hooks/useModulePermission';
import { usePlanEntitlements } from '../../../shell/providers/PlanEntitlementsProvider';
import { useEmployeesStore, withSelfOwner } from '../../../team/store/useEmployeesStore';
import { countCatalogProducts } from '../../../catalog/infra-api';
import type { GetStockMovementHistoryOptions } from '../../../inventory/stock/public-api';
import { isDatabaseUnavailableError } from '../../../shared/localDb';

/** Sesión, permisos y labels compartidos por los panes de inventario. */
export function useInventoryScreenContext() {
  const currentBranchId = useSessionStore((state) => state.currentBranchId);
  const branches = useSessionStore((state) => state.branches);
  const user = useAuthStore((state) => state.user);
  const { canView, canEdit } = useBranchPermission('Inventory');
  const canAdjust = useModulePermission('Inventory', 'adjust');
  const canTransfer = useModulePermission('Inventory', 'transfer');
  const canCount = useModulePermission('Inventory', 'count');
  const canViewValuation = useModulePermission('Inventory', 'view_valuation');
  const { entitlements } = usePlanEntitlements();
  const navigation = useNavigation<any>();
  const [catalogProductCount, setCatalogProductCount] = useState<number | null>(null);
  const employeesRaw = useEmployeesStore((s) => s.employees);
  const selfOwner = useEmployeesStore((s) => s.selfOwner);
  const employees = useMemo(() => withSelfOwner(employeesRaw, selfOwner), [employeesRaw, selfOwner]);

  // #868: restringir team history remoto a owner-only.
  // canView (permiso básico de visualización) era demasiado amplio —
  // cualquier empleado con acceso de ver inventario podía ver el historial
  // de movimientos de todo el equipo. La spec F-06 dice "remote team history si owner".
  const stockHistoryRemoteOptions: GetStockMovementHistoryOptions = useMemo(
    () => ({ useRemoteTeamHistory: entitlements?.is_owner === true }),
    [entitlements?.is_owner]
  );

  const actorLabelByAuthId = useMemo(() => {
    const map: Record<string, string> = {};
    if (user?.id) {
      map[user.id] = user.email ?? user.id;
    }
    for (const e of employees) {
      if (e.authUserId) {
        map[e.authUserId] = e.name;
      }
    }
    return map;
  }, [user, employees]);

  const currentBranchName = useMemo(
    () => branches.find((b) => b.id === currentBranchId)?.name ?? currentBranchId ?? '',
    [branches, currentBranchId]
  );

  // Nunca exponer el UUID crudo: sin nombre resoluble se muestra un label neutro.
  const actorLabel = useCallback(
    (uid: string | null | undefined) => {
      if (!uid) return '';
      return actorLabelByAuthId[uid] ?? 'Equipo';
    },
    [actorLabelByAuthId]
  );

  const destinationBranches = useMemo(
    () => branches.filter((branch) => branch.id !== currentBranchId),
    [branches, currentBranchId]
  );

  const loadCatalogProductCount = useCallback(async () => {
    try {
      const n = await countCatalogProducts({ search: '', status: 'active', category: 'all', itemType: 'physical' });
      setCatalogProductCount(n);
    } catch (e) {
      if (!isDatabaseUnavailableError(e)) {
        console.warn('[Inventory] No se pudo contar productos del catálogo', e);
      }
      setCatalogProductCount(null);
    }
  }, []);

  useEffect(() => {
    void loadCatalogProductCount();
  }, [loadCatalogProductCount]);

  const openCatalog = () => navigation.navigate('Catalog' as never);

  return {
    currentBranchId,
    branches,
    user,
    canEdit,
    canAdjust,
    canTransfer,
    canCount,
    canViewValuation,
    navigation,
    catalogProductCount,
    /** Reintenta el conteo — `catalogProductCount === null` = error/unknown, no cero. */
    retryCatalogProductCount: loadCatalogProductCount,
    stockHistoryRemoteOptions,
    currentBranchName,
    actorLabel,
    destinationBranches,
    openCatalog,
  };
}
